'use client'

import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import closeIcon from '@public/images/close_white.svg'
import { useKeenSlider } from 'keen-slider/react'
import Image from 'next/image'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6SubNavItem } from '@src/components/TopNavV6/TopNavV6Props'
import { TopNavV6Tile } from '@src/components/TopNavV6/TopNavV6Tile'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

interface TopNavV6MobileThirdLevelMenuProps {
  subNavItem: TopNavV6SubNavItem
}

export const TopNavV6MobileThirdLevelMenu = ({ subNavItem }: TopNavV6MobileThirdLevelMenuProps) => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const { setSelectedThirdLevelMenu, setIsMobileMenuOpen } = useTopNavV6()

  const handleBack = () => {
    setSelectedThirdLevelMenu(null)
  }

  const handleClose = () => {
    setSelectedThirdLevelMenu(null)
    setIsMobileMenuOpen(false)
  }

  const handleItemClick = (href?: string) => {
    if (href) {
      trackSelectNavCta({ 'cta url': href })
      setIsMobileMenuOpen(false)
      setSelectedThirdLevelMenu(null)
    }
  }

  const thirdLevelMenu = subNavItem.navItems
  const violators = (thirdLevelMenu?.violators ?? []).filter(
    (violator) => violator.isVisible !== false
  )

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'free',
    rubberband: false,
    slides: {
      perView: 'auto',
      origin: 'auto',
      spacing: 12,
    },
  })

  if (!thirdLevelMenu) return null

  const isSingleViolator = violators.length === 1

  const renderSpacer = () => {
    if (isSingleViolator) return null
    return <div className="keen-slider__slide max-w-1 min-w-1 sm:max-w-5 sm:min-w-5"></div>
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-12 items-center justify-between bg-gradient-to-r from-white/[0.08] to-white/[0.08] px-4">
        <button
          className="flex size-6 appearance-none items-center justify-center"
          onClick={handleBack}
          data-testid="backThirdLevelMenu"
        >
          <ChevronLeftIcon className="size-6 text-bodi-green-bright-500" />
        </button>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-body-md leading-[18px] font-semibold text-bodi-gray-butters uppercase">
            {subNavItem.title}
          </p>
        </div>
        <button
          className="flex size-6 appearance-none items-center justify-center"
          onClick={handleClose}
          data-testid="closeThirdLevelMenu"
        >
          <Image src={closeIcon} width={20} height={20} alt="Close menu" />
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-8 pb-8">
        {thirdLevelMenu.columns.map((column, columnIndex) => (
          <div key={`column-${columnIndex}`} className="flex flex-col">
            {/* Column Title */}
            {column.title && (
              <div className="mb-2 pb-2">
                <p className="text-body-base leading-6 font-medium text-white">{column.title}</p>
              </div>
            )}

            {/* Column Items */}
            {column.items && column.items.length > 0 && (
              <div className="flex flex-col gap-3">
                {column.items.map((item, itemIndex) => {
                  // Check if item is a "View All" or "All" type item (green text)
                  const isViewAllItem =
                    (item.isShopAll ?? false) ||
                    item.title.toLowerCase().includes('view all') ||
                    item.title.toLowerCase().startsWith('all ')

                  return (
                    <button
                      key={`item-${itemIndex}`}
                      className="flex w-full items-center text-left"
                      onClick={() => {
                        handleItemClick(item.href)
                        item.onClick?.()
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <p
                          className={cn(
                            'text-body-sm leading-5',
                            isViewAllItem ? 'text-bodi-green-bright-500' : 'text-bodi-gray-butters'
                          )}
                        >
                          {item.title}
                        </p>
                        {item.badge && (
                          <div className="flex items-center gap-1 text-body-sm leading-5">
                            <span className="font-sans font-normal text-bodi-gray-butters">-</span>
                            <span className="text-center font-sans font-medium text-bodi-green-bright-500">
                              {item.badge}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ))}

        {/* Violators Slider */}
        {violators.length > 0 && (
          <div className="-mx-4">
            <div ref={sliderRef} className="keen-slider px-4">
              {renderSpacer()}
              {violators.map((violator, i) => {
                const effectiveMobileColumns = isSingleViolator ? 1 : 1.25 // peeking effect
                const width = `${100 / effectiveMobileColumns}%`

                return (
                  <div
                    key={i}
                    className="keen-slider__slide"
                    style={{ minWidth: width, maxWidth: width }}
                  >
                    <TopNavV6Tile tile={violator} />
                  </div>
                )
              })}
              {renderSpacer()}
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA Button */}
      {subNavItem.ctaButton && (
        <div className="bg-bodi-black-text px-4 py-5">
          {subNavItem.ctaButton.href ? (
            <LocalizedLink
              href={subNavItem.ctaButton.href}
              onClick={() => {
                handleItemClick(subNavItem.ctaButton!.href)
                subNavItem.ctaButton?.onClick?.()
              }}
            >
              <Button
                className="w-full rounded-full"
                contentClassName="tracking-normal"
                size="medium"
                variant="primary"
              >
                {subNavItem.ctaButton.title}
              </Button>
            </LocalizedLink>
          ) : (
            <button
              onClick={() => {
                subNavItem.ctaButton?.onClick?.()
              }}
              className="w-full"
            >
              <Button
                className="w-full rounded-full"
                contentClassName="tracking-normal"
                size="medium"
                variant="primary"
              >
                {subNavItem.ctaButton.title}
              </Button>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
